import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { EventService, QueryState, RoutingService } from '@spartacus/core';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import {
  Article,
  Breadcrumb,
  Certification,
  CmsTabContainerComponent,
  EntityMap,
  Facet,
  PdpTabTypes,
  Product,
  ProductAttributeTable,
  ProductCertificateGroup,
  ProductDetailsTopic,
  ProductDocument,
  SolrPagination,
  SolrSearchResult,
  SolrSearchSortingDefaults,
} from '../../../../../core/model';
import { ProductCertificateGroupCustomizationEnum } from '../../../../../core/occ/occ-models/occ-generated.models';
import { SearchService } from '../../../../../core/search';
import { PrincipalConfigurationService, ViewCatalogArticlesEvent } from '../../../../../core/user';
import { deepEqualArrays, prepareUrlForLink, sanitizeArticleCode } from '../../../../../core/util';
import { WindowRef } from '../../../../../core/window';
import { DrawerService, DrawerWidth } from '../../../../../shared';
import { Option } from '../../../../../shared/components/dropdown/dropdown.component';
import { DocumentsService } from '../../../../documents-base';
import { ItemListType } from '../../../../tracking/events/tracking-events';
import { AssortmentTable } from '../../../base/models';
import { ProductDetailsService } from '../../../base/services';
import { CatalogSearchService, CatalogTabTypes } from '../../../container/catalog/catalog-search.service';

@Component({
  selector: 'py-pdp-article-list-tabs',
  templateUrl: './pdp-article-list-tabs.component.html',
  styleUrls: ['./pdp-article-list-tabs.component.scss'],
  providers: [ProductDetailsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PdpArticleListTabsComponent implements OnInit, OnDestroy {
  @ViewChild('drawerTemplate') drawerTemplate: TemplateRef<any>;

  readonly sortDefaultOption = SolrSearchSortingDefaults.PDP_TYPE_SKU_PICKER;
  readonly productDetailsTopic = ProductDetailsTopic;

  product$ = new BehaviorSubject<Product>(undefined);
  article$ = new BehaviorSubject<Article>(undefined);
  activeTab$ = new BehaviorSubject<PdpTabTypes | string>(undefined);

  searchLoading$ = this.catalogSearchService.loading$;
  searchResults$: Observable<SolrSearchResult> = this.catalogSearchService.results$;
  sortDisabled$ = new BehaviorSubject<boolean>(false);

  articleRefs$: Observable<string[]>;
  articleListQueryParams$: Observable<EntityMap<string>>;

  assortmentTableState$: Observable<QueryState<AssortmentTable>>;
  washingInstructionIcons$: Observable<Certification[]>;
  washingInstructionGroups$: Observable<ProductCertificateGroup[]>;
  productCertificationGroups$: Observable<ProductCertificateGroup[]>;
  printingTechnologiesGroups$: Observable<ProductCertificateGroup[]>;
  productDocuments$: Observable<ProductDocument[]>;
  technicalAttributesTableState$: Observable<QueryState<ProductAttributeTable>>;

  facets$: Observable<Facet[]>;
  showFacets$: Observable<boolean>;
  customFilterCount$: Observable<number>;

  pagination$: Observable<SolrPagination>;
  breadcrumbs$: Observable<Breadcrumb[]>;
  isBelowLargeScreen$: Observable<boolean>;
  subscription = new Subscription();
  paginationOptions: Option[];
  sortAsc = true;

  activeTabIndex$ = of(CatalogTabTypes.Articles);

  private initialExpandedFacets: Facet[] = [];
  private initialShowAllFacets: { [key: string]: boolean } = {};

  readonly initialPageSize = '10';

  formGroup = new UntypedFormGroup({
    pageSize: new UntypedFormControl('10', []),
  });

  @Input() set product(product: Product) {
    this.product$.next(product);
    this.productDetailsService.setProduct(product);
  }
  @Input() set article(article: Article) {
    this.article$.next(article);
  }
  @Input() set activeTab(activeTab: PdpTabTypes | string) {
    this.activeTab$.next(activeTab);
  }

  @Input() tabContainers: CmsTabContainerComponent[];

  @Output() selectTab = new EventEmitter<PdpTabTypes>();

  PdpTabTypes: typeof PdpTabTypes = PdpTabTypes;
  ltemListTypePDP = ItemListType.PDP_ARTICLES_LIST;

  constructor(
    private activatedRoute: ActivatedRoute,
    private documentsService: DocumentsService,
    private catalogSearchService: CatalogSearchService,
    private drawerService: DrawerService,
    private breakpointService: BreakpointService,
    private routingService: RoutingService,
    private windowRef: WindowRef,
    private principalConfigurationService: PrincipalConfigurationService,
    private searchService: SearchService,
    private eventService: EventService,
    private productDetailsService: ProductDetailsService
  ) {}

  ngOnDestroy(): void {
    //Close filter drawer when we leave article list tabs
    this.drawerService.closeOpenDrawer();
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    const articleQueryParam$ = this.activatedRoute.queryParamMap.pipe(map((params) => params?.get('a')));
    const isPDPTypeArticle$ = articleQueryParam$.pipe(map((param) => Boolean(param)));

    this.setPagination();

    this.articleRefs$ = articleQueryParam$.pipe(
      switchMap((_articleCode) =>
        combineLatest([this.searchLoading$, this.searchResults$, this.product$]).pipe(
          filter(([loading, { articleRefs }]) => !loading && !!articleRefs),
          switchMap(([_loading, { articleRefs }, product]) => {
            if (articleRefs?.length > 0) {
              this.sortDisabled$.next(false);
              return of(articleRefs);
            }
            // The search failed, use article data from product as fallback
            // (the sort button etc will not work as it uses the search backend for that)
            this.sortDisabled$.next(true);
            return of(product?.articles ?? []);
          }),
          tap((articleRefs) => this.eventService.dispatch(new ViewCatalogArticlesEvent(articleRefs)))
        )
      )
    );

    this.productDocuments$ = combineLatest([this.product$, this.article$]).pipe(
      switchMap(([product, article]) => this.documentsService.getProductDocuments(product?.code, article?.code))
    );

    this.pagination$ = this.searchResults$.pipe(map(({ pagination }) => pagination));

    this.breadcrumbs$ = this.searchResults$.pipe(
      filter((searchResults) => !!searchResults?.breadcrumbs),
      map((searchResults) =>
        searchResults.breadcrumbs.map((breadcrumb) => ({
          ...breadcrumb,
          facetValueName: this.getDisplayedValue(
            searchResults.facets.find((facet) => facet.code === breadcrumb.facetCode),
            breadcrumb
          ),
        }))
      ),
      map((breadcrumbs) =>
        breadcrumbs.filter((breadcrumb) => !['allCategories', 'productCode', 'articleCode'].includes(breadcrumb.facetCode))
      )
    );

    this.articleListQueryParams$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('mya')),
      map((myaValue) => (!!myaValue ? { mya: myaValue } : {}))
    );

    this.assortmentTableState$ = this.activeTab$.pipe(
      filter((activeTab) => activeTab === PdpTabTypes.Assortment),
      switchMap(() => this.productDetailsService.getAssortmentTableState())
    );

    this.technicalAttributesTableState$ = this.activeTab$.pipe(
      filter((activeTab) => activeTab === PdpTabTypes.TechnicalAttributes),
      switchMap(() => this.productDetailsService.getTechnicalAttributesState())
    );

    const certLabels$ = combineLatest([this.article$, this.product$]).pipe(
      switchMap(([article]) => (!!article ? of(article?.certLabels) : this.productDetailsService.getCertLabels())),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.washingInstructionGroups$ = certLabels$.pipe(
      map((certLabels) =>
        certLabels?.groups?.filter(
          (group) => group.customization === ProductCertificateGroupCustomizationEnum.WASHING_INSTRUCTIONS
        )
      ),
      map((groups) => groups?.filter((group) => !!group.certificates?.length)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.washingInstructionIcons$ = this.washingInstructionGroups$.pipe(
      map((groups) => groups?.flatMap((group) => group?.certificates) || []),
      map((certificates) => certificates?.filter((certificate) => !!certificate.icon))
    );

    this.productCertificationGroups$ = certLabels$.pipe(
      map((certLabels) =>
        certLabels?.groups?.filter((group) => group.customization === ProductCertificateGroupCustomizationEnum.DEFAULT)
      ),
      map((groups) => groups?.filter((group) => !!group.certificates?.length))
    );

    this.printingTechnologiesGroups$ = certLabels$.pipe(
      map((certLabels) =>
        certLabels?.groups?.filter(
          (group) => group.customization === ProductCertificateGroupCustomizationEnum.PRINTING_TECHNOLOGY
        )
      ),
      map((groups) => groups?.filter((group) => !!group.certificates?.length))
    );

    this.facets$ = this.searchResults$.pipe(
      filter((res) => !!res?.facets),
      map((searchResults) => this.catalogSearchService.getFacets(searchResults)),
      distinctUntilChanged(deepEqualArrays),
      tap((facets) => {
        this.initialExpandedFacets = facets?.filter((f) => f?.values[0].name?.toLowerCase() !== 'true').slice(0, 3);
      })
    );

    this.subscription.add(this.catalogSearchService.searchByRouting$.subscribe());

    this.customFilterCount$ = this.facets$.pipe(
      map((facets) => {
        let count = 0;

        if (facets) {
          const facetsCount = facets.reduce(
            (total, facet) => total + facet.values.reduce((t, currentValue) => t + (currentValue.selected ? 1 : 0), 0),
            0
          );
          count = count + facetsCount;
        }
        return count;
      })
    );

    this.showFacets$ = combineLatest([isPDPTypeArticle$, this.product$, this.facets$]).pipe(
      map(([isPDPTypeArticle, product, facets]) => product?.articles?.length > 6 && !isPDPTypeArticle && !!facets.length)
    );

    this.isBelowLargeScreen$ = this.breakpointService.isDown(BREAKPOINT.lg);
  }

  getDisplayedValue(facet: Facet, breadcrumb: Breadcrumb): string {
    if (facet?.values?.length === 1 && facet?.values[0].name.toLowerCase() === 'true') {
      return facet.name;
    }

    return facet?.values?.find((value) => value.code === breadcrumb.facetValueCode)?.name;
  }

  handleChange($event): void {
    this.catalogSearchService.setQuery($event);
  }

  nextPage($event: number): void {
    this.catalogSearchService.loadMore($event);
  }

  openDrawer(): void {
    this.drawerService
      .open({
        template: this.drawerTemplate,
        width: DrawerWidth.NearFull,
        closeOnNavigation: false,
      })
      .pipe(take(1))
      .subscribe();
  }

  onResetFacets(): void {
    this.catalogSearchService.resetPDPFilters();
    this.drawerService.closeOpenDrawer();
  }

  handleShowAll($event: boolean, facet: Facet): void {
    if (facet.code) {
      this.initialShowAllFacets = { ...this.initialShowAllFacets, [facet.code]: $event };
    }
  }

  initialShowAll(facet: Facet): boolean {
    return facet.code ? !!this.initialShowAllFacets[facet.code] : false;
  }

  initialCollapsed(facet: Facet): boolean {
    return this.initialExpandedFacets.find((f) => f === facet) ||
      (facet.values[0].name.toLowerCase() !== 'true' && facet.values.filter((v) => v.selected).length > 0)
      ? false
      : true;
  }

  goToPrintProductArticles(productName: string, productCode: string, categoryRef: string) {
    productName = prepareUrlForLink(`${productName}--p${productCode}`);

    this.routingService.go(
      { cxRoute: 'product', params: { productName } },
      {
        queryParams: {
          sort: this.activatedRoute.snapshot.queryParams['sort'],
          query: this.activatedRoute.snapshot.queryParams['query'],
          a: sanitizeArticleCode(this.activatedRoute.snapshot.queryParams['a']),
          c: categoryRef,
        },
      }
    );
  }

  printAssortment() {
    this.windowRef.nativeWindow.print();
  }

  private setPagination(): void {
    let isFirstPageSizeChange = true;

    this.formGroup
      .get('pageSize')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((val) => {
        if (!isFirstPageSizeChange) {
          this.searchService.clearSearch();
        }

        isFirstPageSizeChange = false;

        this.catalogSearchService.setPageSize(val);
      });

    this.subscription.add(
      this.principalConfigurationService.getValue('articleFetchRowsForPdp').subscribe((pc) => {
        if (!pc?.value) {
          return this.catalogSearchService.setPageSize(Number(this.initialPageSize));
        }

        const pageSize = pc.value as number;
        const firstPaginationOption = pageSize.toString();
        // generating second pagination option to be 2.5 times larger than option from the principal config, e.g. 10, 25
        const secondPaginationOption = Math.floor(pageSize * 2.5).toString();

        this.paginationOptions = [
          { value: firstPaginationOption, label: firstPaginationOption },
          { value: secondPaginationOption, label: secondPaginationOption },
        ];

        this.formGroup.get('pageSize').setValue(firstPaginationOption);
      })
    );
  }
}
